import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CreditCard, CheckCircle2, Clock, AlertCircle, Trash2, 
  CalendarClock, DollarSign, Check, User, MapPin, ChevronRight, Eye, Filter, RefreshCw, Undo2,
  Search, FileDown, FileText, Copy, Bell, Send, ChevronDown, ChevronUp, Mail, MailX
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateSafe = (dateStr, options = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  if (!dateStr) return 'N/A';
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString('es-MX', options);
};

// Función para descargar PDFs directamente (sin Adobe)
const downloadPDF = async (url, filename) => {
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Error al descargar');
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error descargando PDF:', error);
    toast.error('Error al descargar el PDF');
  }
};

export default function Pagos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tratos, setTratos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  // Recuperar filtro guardado de localStorage
  const [selectedFraccId, setSelectedFraccId] = useState(() => {
    return localStorage.getItem('filtro_fraccionamiento') || 'todos';
  });
  const [loading, setLoading] = useState(true);
  
  // Búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  
  // Guardar filtro en localStorage cuando cambie
  const handleFraccFilter = (value) => {
    setSelectedFraccId(value);
    localStorage.setItem('filtro_fraccionamiento', value);
  };
  
  const [selectedTrato, setSelectedTrato] = useState(null);
  const [pagosDelTrato, setPagosDelTrato] = useState([]);
  const [pagosDialogOpen, setPagosDialogOpen] = useState(false);
  
  const [quickPayDialogOpen, setQuickPayDialogOpen] = useState(false);
  const [reprogramDialogOpen, setReprogramDialogOpen] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, pago: null });
  const [revertDialog, setRevertDialog] = useState({ open: false, pago: null });
  
  // Estado para editar fecha de pago
  const [editFechaDialogOpen, setEditFechaDialogOpen] = useState(false);
  const [editFechaData, setEditFechaData] = useState({ pago: null, nueva_fecha: '' });
  
  const [quickPayFormData, setQuickPayFormData] = useState({ 
    monto: '', 
    metodo_pago: 'efectivo',
    fecha_pago: new Date().toISOString().split('T')[0],
    moneda: 'USD',
    moneda_pago: 'USD',  // Moneda en que se recibió el pago
    tipo_cambio: 17.50,
    tipo_pago_parcial: false
  });
  const [reprogramFormData, setReprogramFormData] = useState({ nueva_fecha: '' });
  
  // Estado para tipo de cambio
  const [tipoCambioActual, setTipoCambioActual] = useState(17.50);
  const [loadingTipoCambio, setLoadingTipoCambio] = useState(false);

  // Panel de recordatorios
  const [reminderPanel, setReminderPanel] = useState({ open: true, loading: false, data: null, sending: false });

  // Obtener tipo de cambio al cargar
  useEffect(() => {
    const fetchTipoCambio = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setTipoCambioActual(data.tipo_cambio);
          setQuickPayFormData(prev => ({ ...prev, tipo_cambio: data.tipo_cambio }));
        }
      } catch (error) {
        console.error('Error al obtener tipo de cambio');
      }
    };
    fetchTipoCambio();
  }, []);
  
  // Actualizar tipo de cambio manualmente
  const actualizarTipoCambio = async () => {
    setLoadingTipoCambio(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTipoCambioActual(data.tipo_cambio);
        setQuickPayFormData(prev => ({ ...prev, tipo_cambio: data.tipo_cambio }));
        toast.success(`Tipo de cambio: $${data.tipo_cambio} MXN/USD`);
      }
    } catch (error) {
      toast.error('Error al actualizar tipo de cambio');
    } finally {
      setLoadingTipoCambio(false);
    }
  };

  const fetchReminderLogs = async () => {
    setReminderPanel(p => ({ ...p, loading: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/notificaciones/logs-recordatorios`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setReminderPanel(p => ({ ...p, data, loading: false }));
      } else {
        setReminderPanel(p => ({ ...p, loading: false }));
      }
    } catch {
      setReminderPanel(p => ({ ...p, loading: false }));
    }
  };

  const handleSendReminders = async () => {
    setReminderPanel(p => ({ ...p, sending: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/notificaciones/enviar-recordatorios-hoy`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.enviados} recordatorio(s) enviado(s)`);
        if (data.clientes_sin_email?.length > 0) {
          toast.warning(`${data.clientes_sin_email.length} cliente(s) sin email: ${data.clientes_sin_email.join(', ')}`);
        }
        fetchReminderLogs();
      } else {
        toast.error(data.detail || 'Error al enviar');
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setReminderPanel(p => ({ ...p, sending: false }));
    }
  };

  useEffect(() => {
    fetchData();
    fetchReminderLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detectar parámetros de URL para abrir trato/pago específico
  useEffect(() => {
    const tratoId = searchParams.get('trato');
    const pagoIdParam = searchParams.get('pago');
    
    if (tratoId && tratos.length > 0) {
      const trato = tratos.find(t => t.trato_id === tratoId);
      if (trato) {
        handleOpenPagos(trato, pagoIdParam);
        // Limpiar parámetros de URL
        setSearchParams({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tratos, searchParams]);

  const fetchData = async () => {
    try {
      const [tratosRes, clientesRes, lotesRes, fraccsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tratos/con-deuda`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/clientes`, { credentials: 'include' }),
        // 🔥 Obtener TODOS los lotes sin filtro de fraccionamiento para poder filtrar en frontend
        fetch(`${BACKEND_URL}/api/lotes?all=true`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
      ]);
      
      if (!tratosRes.ok || !clientesRes.ok || !lotesRes.ok || !fraccsRes.ok) throw new Error('Error');
      
      const [tratosData, clientesData, lotesData, fraccsData] = await Promise.all([
        tratosRes.json(), clientesRes.json(), lotesRes.json(), fraccsRes.json()
      ]);
      
      setTratos(tratosData);
      setClientes(clientesData);
      setLotes(lotesData);
      setFraccionamientos(fraccsData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPagos = async (trato, highlightPagoId = null) => {
    setSelectedTrato(trato);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos?trato_id=${trato.trato_id}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error');
      const data = await response.json();
      data.sort((a, b) => a.numero_pago - b.numero_pago);
      setPagosDelTrato(data);
      setPagosDialogOpen(true);
      
      // Si hay un pago específico para resaltar, abrirlo automáticamente
      if (highlightPagoId) {
        const pagoToHighlight = data.find(p => p.pago_id === highlightPagoId);
        if (pagoToHighlight) {
          // Pequeño delay para que se abra el modal de pagos primero
          setTimeout(() => {
            setSelectedPago(pagoToHighlight);
            const pendiente = pagoToHighlight.monto_programado - (pagoToHighlight.monto_pagado || 0);
            const interesAcumulado = pagoToHighlight.interes_pendiente || 0;
            const totalSugerido = pendiente + interesAcumulado;
            const monedaDelTrato = trato?.moneda === 'MXN' ? 'MXN' : 'USD';
            
            setQuickPayFormData({ 
              monto: totalSugerido.toFixed(2), 
              metodo_pago: 'efectivo',
              fecha_pago: new Date().toISOString().split('T')[0],
              moneda: monedaDelTrato,
              moneda_pago: monedaDelTrato,
              tipo_cambio: tipoCambioActual,
              tipo_pago_parcial: false
            });
            setQuickPayDialogOpen(true);
          }, 400);
        }
      }
    } catch (error) {
      toast.error('Error al cargar pagos');
    }
  };

  const reloadPagos = async () => {
    if (!selectedTrato) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos?trato_id=${selectedTrato.trato_id}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error');
      const data = await response.json();
      data.sort((a, b) => a.numero_pago - b.numero_pago);
      setPagosDelTrato(data);
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  // Estado para modal de marcar pagado con fecha
  const [marcarPagadoDialog, setMarcarPagadoDialog] = useState({ open: false, pago: null });
  const [marcarPagadoFecha, setMarcarPagadoFecha] = useState(new Date().toISOString().split('T')[0]);
  const [marcarPagadoMetodo, setMarcarPagadoMetodo] = useState('efectivo');
  // Estados para conversión de moneda en marcar pagado
  const [marcarPagadoMoneda, setMarcarPagadoMoneda] = useState('USD');  // Moneda en que pagó el cliente
  const [marcarPagadoTipoCambio, setMarcarPagadoTipoCambio] = useState(17.50);
  const [marcarPagadoMontoOriginal, setMarcarPagadoMontoOriginal] = useState(0);  // Monto en la moneda que pagó

  // Estado para pagaré
  const handleTogglePagare = async (pago) => {
    try {
      const nuevoEstadoPagare = !pago.pagare_entregado;
      const response = await fetch(`${BACKEND_URL}/api/pagos/${pago.pago_id}/pagare`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pagare_entregado: nuevoEstadoPagare })
      });
      
      if (!response.ok) {
        throw new Error('Error al actualizar pagaré');
      }
      
      toast.success(nuevoEstadoPagare ? 'Pagaré marcado como entregado' : 'Pagaré desmarcado');
      // Recargar los pagos del trato actual
      if (selectedTrato) {
        handleOpenPagos(selectedTrato);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleOpenMarcarPagado = (pago) => {
    const pendiente = pago.monto_programado - (pago.monto_pagado || 0);
    const interesAcumulado = pago.interes_pendiente || 0;
    
    if (pendiente <= 0 && interesAcumulado <= 0) {
      toast.info('Este pago ya está completo');
      return;
    }
    
    // Sugerir la fecha programada del pago o la fecha actual
    const fechaSugerida = pago.fecha_programada 
      ? pago.fecha_programada.split('T')[0] 
      : new Date().toISOString().split('T')[0];
    
    // Obtener la moneda del trato
    const monedaDelTrato = selectedTrato?.moneda || 'USD';
    const totalAPagar = pendiente + interesAcumulado;
    
    setMarcarPagadoFecha(fechaSugerida);
    setMarcarPagadoMetodo('efectivo');
    // Por defecto, el cliente paga en la misma moneda del trato
    setMarcarPagadoMoneda(monedaDelTrato);
    setMarcarPagadoTipoCambio(tipoCambioActual);
    setMarcarPagadoMontoOriginal(totalAPagar);  // Monto en la moneda del trato
    setMarcarPagadoDialog({ open: true, pago });
  };

  const handleConfirmarPagado = async () => {
    const pago = marcarPagadoDialog.pago;
    if (!pago) return;
    
    const pendiente = pago.monto_programado - (pago.monto_pagado || 0);
    const interesAcumulado = pago.interes_pendiente || 0;
    const totalAPagarEnMonedaTrato = pendiente + interesAcumulado;
    
    // Obtener moneda del trato
    const monedaTrato = selectedTrato?.moneda || 'USD';
    const monedaPago = marcarPagadoMoneda;  // Moneda en que pagó el cliente
    const tipoCambio = parseFloat(marcarPagadoTipoCambio) || 17.50;
    const montoOriginal = parseFloat(marcarPagadoMontoOriginal) || totalAPagarEnMonedaTrato;
    
    // Calcular monto equivalente en la moneda del trato
    let montoEnMonedaTrato;
    if (monedaPago === monedaTrato) {
      // Misma moneda - no hay conversión
      montoEnMonedaTrato = montoOriginal;
    } else if (monedaPago === 'MXN' && monedaTrato === 'USD') {
      // Pagó en pesos, trato en dólares -> dividir
      montoEnMonedaTrato = montoOriginal / tipoCambio;
    } else if (monedaPago === 'USD' && monedaTrato === 'MXN') {
      // Pagó en dólares, trato en pesos -> multiplicar
      montoEnMonedaTrato = montoOriginal * tipoCambio;
    } else {
      montoEnMonedaTrato = montoOriginal;
    }
    
    try {
      const payload = {
        monto_pagado: montoEnMonedaTrato,  // Siempre en la moneda del trato
        fecha_pago: new Date(marcarPagadoFecha).toISOString(),
        metodo_pago: marcarPagadoMetodo,
        // Metadata de trazabilidad (SIEMPRE se guarda)
        moneda: monedaTrato,
        moneda_pago: monedaPago,
        monto_pagado_original: montoOriginal,
        tipo_cambio: tipoCambio
      };
      
      const response = await fetch(`${BACKEND_URL}/api/pagos/${pago.pago_id}/registrar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Error');
      
      // Mensaje de confirmación más detallado
      if (monedaPago !== monedaTrato) {
        const simboloPago = monedaPago === 'MXN' ? 'MXN' : 'USD';
        const simboloTrato = monedaTrato === 'MXN' ? 'MXN' : 'USD';
        toast.success(`Pago registrado: ${simboloPago} $${montoOriginal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} → ${simboloTrato} $${montoEnMonedaTrato.toLocaleString('es-MX', { minimumFractionDigits: 2 })} (TC: ${tipoCambio})`);
      } else if (interesAcumulado > 0) {
        toast.success(`Pago completado (incluye $${interesAcumulado.toLocaleString('es-MX', { minimumFractionDigits: 2 })} de interés por atraso)`);
      } else {
        toast.success('Pago marcado como PAGADO');
      }
      setMarcarPagadoDialog({ open: false, pago: null });
      reloadPagos();
    } catch (error) {
      toast.error('Error al marcar como pagado');
    }
  };

  const handleOpenQuickPay = (pago) => {
    setSelectedPago(pago);
    const pendiente = pago.monto_programado - (pago.monto_pagado || 0);
    const interesAcumulado = pago.interes_pendiente || 0;
    const totalSugerido = pendiente + interesAcumulado;
    
    // Detectar la moneda del trato
    const monedaDelTrato = selectedTrato?.moneda === 'MXN' ? 'MXN' : 'USD';
    
    setQuickPayFormData({ 
      monto: totalSugerido.toFixed(2), 
      metodo_pago: 'efectivo',
      fecha_pago: new Date().toISOString().split('T')[0],
      moneda: monedaDelTrato,
      moneda_pago: monedaDelTrato,  // Por defecto, pagar en la misma moneda del trato
      tipo_cambio: tipoCambioActual,
      tipo_pago_parcial: false
    });
    setQuickPayDialogOpen(true);
  };

  const handleQuickPaySubmit = async (e) => {
    e.preventDefault();
    if (!selectedPago) return;
    
    try {
      const montoIngresado = parseFloat(quickPayFormData.monto);
      const tipoCambio = parseFloat(quickPayFormData.tipo_cambio);
      const monedaPago = quickPayFormData.moneda_pago || 'USD';
      const monedaTrato = selectedTrato?.moneda || 'USD';
      
      // Calcular el monto en la moneda del trato
      let montoEnMonedaTrato;
      let montoUSD;
      let montoMXN;
      
      if (monedaPago === 'MXN') {
        // Pagó en pesos
        montoMXN = montoIngresado;
        montoUSD = montoIngresado / tipoCambio;
        montoEnMonedaTrato = monedaTrato === 'MXN' ? montoMXN : montoUSD;
      } else {
        // Pagó en dólares
        montoUSD = montoIngresado;
        montoMXN = montoIngresado * tipoCambio;
        montoEnMonedaTrato = monedaTrato === 'USD' ? montoUSD : montoMXN;
      }
      
      const payload = {
        monto_pagado: montoEnMonedaTrato,  // Guardar en la moneda del trato
        monto_pagado_original: montoIngresado,  // Monto original que pagó
        moneda_pago: monedaPago,  // En qué moneda pagó
        fecha_pago: new Date(quickPayFormData.fecha_pago).toISOString(),
        metodo_pago: quickPayFormData.metodo_pago,
        moneda: monedaTrato,
        tipo_cambio: tipoCambio
      };
      
      const response = await fetch(`${BACKEND_URL}/api/pagos/${selectedPago.pago_id}/registrar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Error');
      
      const tipoMsg = quickPayFormData.tipo_pago_parcial ? 'Abono' : 'Pago';
      toast.success(`${tipoMsg}: $${montoUSD.toFixed(2)} USD = $${montoMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`);
      
      setQuickPayDialogOpen(false);
      reloadPagos();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  const handleOpenReprogram = (pago) => {
    setSelectedPago(pago);
    const nuevaFecha = new Date(pago.fecha_programada);
    nuevaFecha.setDate(nuevaFecha.getDate() + 7);
    setReprogramFormData({ nueva_fecha: nuevaFecha.toISOString().split('T')[0] });
    setReprogramDialogOpen(true);
  };

  const handleReprogramSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPago) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/${selectedPago.pago_id}/reprogramar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fecha_programada: reprogramFormData.nueva_fecha })
      });
      
      if (!response.ok) throw new Error('Error');
      toast.success('Fecha reprogramada');
      setReprogramDialogOpen(false);
      reloadPagos();
    } catch (error) {
      toast.error('Error al reprogramar');
    }
  };

  // Función para abrir modal de editar fecha de pago
  const handleOpenEditFecha = (pago) => {
    const fechaActual = pago.fecha_pago ? pago.fecha_pago.split('T')[0] : new Date().toISOString().split('T')[0];
    setEditFechaData({ pago, nueva_fecha: fechaActual });
    setEditFechaDialogOpen(true);
  };

  // Función para guardar nueva fecha de pago
  const handleEditFechaSubmit = async (e) => {
    e.preventDefault();
    if (!editFechaData.pago) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/${editFechaData.pago.pago_id}/editar-fecha`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fecha_pago: new Date(editFechaData.nueva_fecha).toISOString() })
      });
      
      if (!response.ok) throw new Error('Error');
      toast.success('Fecha de pago actualizada');
      setEditFechaDialogOpen(false);
      reloadPagos();
    } catch (error) {
      toast.error('Error al actualizar fecha');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.pago) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/${deleteDialog.pago.pago_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Error');
      toast.success('Pago eliminado');
      setDeleteDialog({ open: false, pago: null });
      reloadPagos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Función para REVERTIR un pago (quitar el monto pagado)
  const handleRevert = async () => {
    if (!revertDialog.pago) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/${revertDialog.pago.pago_id}/revertir`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al revertir');
      }
      
      toast.success('Pago revertido correctamente');
      setRevertDialog({ open: false, pago: null });
      reloadPagos();
    } catch (error) {
      toast.error(error.message || 'Error al revertir pago');
    }
  };

  const getCliente = (clienteId) => clientes.find(c => c.cliente_id === clienteId);
  const getLote = (loteId) => lotes.find(l => l.lote_id === loteId);

  const calcularEstadoVisual = (pago) => {
    const hoy = new Date();
    hoy.setHours(12, 0, 0, 0);
    
    const fechaStr = pago.fecha_programada?.split('T')[0];
    if (!fechaStr) return 'al_corriente';
    const [year, month, day] = fechaStr.split('-').map(Number);
    const fechaProgramada = new Date(year, month - 1, day, 12, 0, 0);
    
    const diasDiferencia = Math.floor((fechaProgramada - hoy) / (1000 * 60 * 60 * 24));
    const montoPendiente = pago.monto_programado - (pago.monto_pagado || 0);
    
    if (pago.estado === 'pagado' || montoPendiente <= 0) return 'pagado';
    if (diasDiferencia < 0) return 'atrasado';
    if (diasDiferencia === 0) return 'vence_hoy';
    if (diasDiferencia <= 7) return 'proximo';
    return 'al_corriente';
  };

  const getEstadoBadge = (estadoVisual) => {
    const styles = {
      pagado: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'PAGADO' },
      atrasado: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'ATRASADO' },
      vence_hoy: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'VENCE HOY' },
      proximo: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'PRÓXIMO' },
      al_corriente: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'AL DÍA' }
    };
    const style = styles[estadoVisual];
    return (
      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Filtrar tratos por fraccionamiento seleccionado y búsqueda
  const tratosFiltrados = tratos.filter(trato => {
    // Filtro por fraccionamiento
    let matchFracc = true;
    if (selectedFraccId && selectedFraccId !== 'todos') {
      const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
      
      // Si el trato no tiene lotes_ids, incluirlo por defecto
      if (lotesIds.length === 0) {
        return true;
      }
      
      // Verificar si algún lote del trato pertenece al fraccionamiento seleccionado
      matchFracc = lotesIds.some(loteId => {
        const lote = lotes.find(l => l.lote_id === loteId);
        if (!lote) {
          return false;
        }
        return lote.fraccionamiento_id === selectedFraccId;
      });
    }
    
    // Filtro por búsqueda
    let matchSearch = true;
    if (searchTerm) {
      const cliente = clientes.find(c => c.cliente_id === trato.cliente_id);
      const clienteNombre = cliente?.nombre_completo?.toLowerCase() || '';
      const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
      const lotesDelTrato = lotesIds.map(id => lotes.find(l => l.lote_id === id)).filter(Boolean);
      const lotesNumeros = lotesDelTrato.map(l => l.numero).join(' ');
      const manzanas = lotesDelTrato.map(l => l.manzana).join(' ');
      
      const searchLower = searchTerm.toLowerCase();
      matchSearch = clienteNombre.includes(searchLower) || 
                    lotesNumeros.includes(searchTerm) ||
                    manzanas.toLowerCase().includes(searchLower);
    }
    
    return matchFracc && matchSearch;
  });

  return (
    <div data-testid="pagos-page" className="min-h-screen bg-zinc-950 p-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Control de Pagos</h1>
          <p className="text-zinc-400 mt-2">Selecciona un cliente para ver sus pagos</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-pagos"
              placeholder="Buscar cliente, lote..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-[200px] bg-zinc-900 border-zinc-700 text-white"
            />
          </div>
          
          {/* Filtro por Fraccionamiento */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={selectedFraccId} onValueChange={handleFraccFilter}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-700 text-white">
                <SelectValue placeholder="Filtrar por..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="todos" className="text-white hover:bg-zinc-800">Todos los fraccionamientos</SelectItem>
                {fraccionamientos.map((f) => (
                  <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white hover:bg-zinc-800">
                    {f.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Panel de Recordatorios */}
      <div data-testid="reminder-panel" className="bg-zinc-900 border border-zinc-800 rounded-xl mb-6 overflow-hidden">
        <button
          data-testid="reminder-panel-toggle"
          onClick={() => setReminderPanel(p => ({ ...p, open: !p.open }))}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-zinc-800/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Bell className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-white">Recordatorios de Pago</h3>
              <p className="text-xs text-zinc-500">
                {reminderPanel.data ? `${reminderPanel.data.total_hoy} pago(s) vencen hoy` : 'Cargando...'}
              </p>
            </div>
            {reminderPanel.data?.total_hoy > 0 && (
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded-full ml-2">
                {reminderPanel.data.total_hoy}
              </span>
            )}
          </div>
          {reminderPanel.open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>

        {reminderPanel.open && (
          <div className="border-t border-zinc-800 px-5 py-4 space-y-4">
            {reminderPanel.loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reminderPanel.data ? (
              <>
                {/* Resumen + botón enviar */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-4 h-4 text-emerald-400" />
                      <span className="text-zinc-400">Con email:</span>
                      <span className="text-white font-medium">{reminderPanel.data.con_email}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MailX className="w-4 h-4 text-red-400" />
                      <span className="text-zinc-400">Sin email:</span>
                      <span className="text-white font-medium">{reminderPanel.data.sin_email}</span>
                    </div>
                  </div>
                  <Button
                    data-testid="send-reminders-btn"
                    onClick={handleSendReminders}
                    disabled={reminderPanel.sending || reminderPanel.data.con_email === 0}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                  >
                    {reminderPanel.sending ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Enviar Recordatorios Hoy
                  </Button>
                </div>

                {/* Tabla de pagos que vencen hoy */}
                {reminderPanel.data.pagos_hoy.length > 0 ? (
                  <div className="rounded-lg border border-zinc-800 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-800/70">
                        <tr>
                          <th className="text-left px-4 py-2 text-zinc-400 font-medium">Cliente</th>
                          <th className="text-left px-4 py-2 text-zinc-400 font-medium">Email</th>
                          <th className="text-left px-4 py-2 text-zinc-400 font-medium">Lote</th>
                          <th className="text-left px-4 py-2 text-zinc-400 font-medium">Fraccionamiento</th>
                          <th className="text-right px-4 py-2 text-zinc-400 font-medium">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reminderPanel.data.pagos_hoy.map((p, i) => (
                          <tr key={i} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="px-4 py-2 text-white font-medium">{p.cliente_nombre}</td>
                            <td className="px-4 py-2">
                              {p.cliente_email ? (
                                <span className="text-emerald-400 text-xs">{p.cliente_email}</span>
                              ) : (
                                <span className="text-red-400 text-xs">Sin email</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-zinc-300">{p.lote_info}</td>
                            <td className="px-4 py-2 text-zinc-400">{p.fraccionamiento || '—'}</td>
                            <td className="px-4 py-2 text-right font-mono text-amber-400 font-medium">
                              {p.moneda} ${p.monto?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-3">No hay pagos que venzan hoy</p>
                )}

                {/* Historial reciente de envíos */}
                {reminderPanel.data.logs.length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Historial de envíos recientes</p>
                    <div className="flex flex-wrap gap-2">
                      {reminderPanel.data.logs.slice(0, 7).map((log, i) => {
                        const d = new Date(log.fecha);
                        return (
                          <div key={i} className="px-3 py-1.5 bg-zinc-800 rounded-lg text-xs flex items-center gap-2 border border-zinc-700/50">
                            <span className="text-zinc-400">{d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
                            <span className="text-emerald-400 font-medium">{log.enviados} enviados</span>
                            {log.errores > 0 && <span className="text-red-400">{log.errores} error(es)</span>}
                            <span className="text-zinc-600">{log.tipo}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-sm text-center py-3">No se pudieron cargar los datos</p>
            )}
          </div>
        )}
      </div>

      {tratosFiltrados.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <CreditCard className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">
            {tratos.length === 0 ? 'No hay tratos registrados' : 'No hay tratos en este fraccionamiento'}
          </h3>
          <p className="text-zinc-500">
            {tratos.length === 0 ? 'Crea un trato de venta para comenzar' : 'Selecciona otro fraccionamiento'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tratosFiltrados.map((trato) => {
            const cliente = getCliente(trato.cliente_id);
            const progreso = ((trato.total_pagado || 0) / trato.precio_final) * 100;
            
            const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
            const lotesDelTrato = lotesIds.map(id => getLote(id)).filter(Boolean);
            
            let ubicacionLabel = '';
            let metrosCuadrados = 0;
            if (lotesDelTrato.length === 1) {
              const lote = lotesDelTrato[0];
              const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lote?.fraccionamiento_id);
              metrosCuadrados = lote?.superficie || lote?.metros_cuadrados || 0;
              const parts = [`Lote ${lote?.numero || 'N/A'}`];
              if (lote?.manzana) parts.push(`Mz. ${lote.manzana}`);
              if (fracc) parts.push(fracc.nombre);
              ubicacionLabel = parts.join(' · ');
            } else if (lotesDelTrato.length > 1) {
              // Para múltiples lotes, mostrar cada uno con su manzana y sumar m²
              const lotesInfo = lotesDelTrato.map(l => {
                let info = `Lote ${l?.numero || '?'}`;
                if (l?.manzana) info += ` Mz.${l.manzana}`;
                return info;
              }).filter(Boolean).join(', ');
              const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lotesDelTrato[0]?.fraccionamiento_id);
              ubicacionLabel = `${lotesInfo} · ${fracc?.nombre || ''}`;
              metrosCuadrados = lotesDelTrato.reduce((sum, l) => sum + (l?.superficie || l?.metros_cuadrados || 0), 0);
            } else {
              ubicacionLabel = 'Sin lotes';
            }
            
            return (
              <div 
                key={trato.trato_id} 
                className={`bg-zinc-900 border rounded-xl p-6 cursor-pointer transition-all hover:border-amber-500/50 ${
                  (trato.deuda || 0) === 0 ? 'border-emerald-500/30' : 'border-zinc-800'
                }`}
                onClick={() => handleOpenPagos(trato)}
                data-testid={`trato-card-${trato.trato_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-zinc-500" />
                      <h3 className="font-bold text-lg text-white">
                        {cliente?.nombre_completo || 'Cliente'}
                      </h3>
                      {/* Badge de moneda */}
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                        trato.moneda === 'MXN' 
                          ? 'bg-amber-500/20 text-amber-400' 
                          : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {trato.moneda || 'USD'}
                      </span>
                    </div>
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded">
                      {ubicacionLabel}
                      {metrosCuadrados > 0 && (
                        <span className="ml-1 text-cyan-400 font-bold">• {metrosCuadrados} m²</span>
                      )}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600" />
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                  <div>
                    <p className="text-xs text-zinc-500">Enganche</p>
                    <p className="font-mono font-bold text-white">
                      {trato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(trato.enganche || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Pagado</p>
                    <p className="font-mono font-bold text-emerald-400">
                      {trato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(trato.total_pagado || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Deuda</p>
                    <p className={`font-mono font-bold ${(trato.deuda || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {trato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(trato.deuda || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Progreso</span>
                    <span>{Math.round(progreso)}%</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${(trato.deuda || 0) === 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(100, progreso)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">
                    {trato.pagos_realizados || 0}/{trato.pagos_totales || trato.numero_meses} pagos
                  </span>
                  {(trato.deuda || 0) === 0 ? (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full">
                      LIQUIDADO
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                      <Eye className="w-3 h-3 mr-1" />
                      Ver Pagos
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Pagos del Trato */}
      <Dialog open={pagosDialogOpen} onOpenChange={setPagosDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5" />
                {selectedTrato && getCliente(selectedTrato.cliente_id)?.nombre_completo}
                {/* Badge de moneda */}
                {selectedTrato && (
                  <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                    selectedTrato.moneda === 'MXN' 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {selectedTrato.moneda === 'MXN' ? '🇲🇽 MXN' : '🇺🇸 USD'}
                  </span>
                )}
              </div>
              {/* Botón descargar comprobante */}
              {selectedTrato && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const cliente = clientes.find(c => c.cliente_id === selectedTrato.cliente_id);
                    const clienteNombre = cliente?.nombre_completo?.replace(/\s+/g, '_') || 'Cliente';
                    downloadPDF(
                      `${BACKEND_URL}/api/tratos/${selectedTrato.trato_id}/comprobante`,
                      `Comprobante_${clienteNombre}.pdf`
                    );
                  }}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Descargar Comprobante
                </Button>
              )}
              {/* Botón copiar mensaje para cliente */}
              {selectedTrato && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="copy-invoice-message"
                  onClick={() => {
                    const cliente = clientes.find(c => c.cliente_id === selectedTrato.cliente_id);
                    const fracc = fraccionamientos.find(f => f.fraccionamiento_id === selectedTrato.fraccionamiento_id);
                    const loteIds = selectedTrato.lotes_ids || [];
                    const loteLabel = loteIds.length > 0 ? `Lote ${loteIds.join(', ')}` : 'Lote';
                    const monedaSimbolo = selectedTrato.moneda === 'MXN' ? 'MXN' : 'USD';
                    const msg = `Estimado cliente${cliente ? ` ${cliente.nombre_completo}` : ''}, adjunto el comprobante del ${loteLabel} en ${fracc?.nombre || 'el fraccionamiento'} por ${monedaSimbolo} $${(selectedTrato.precio_final || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}.\nGracias por su pago.`;
                    navigator.clipboard.writeText(msg).then(() => toast.success('Mensaje copiado')).catch(() => toast.error('Error al copiar'));
                  }}
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Mensaje
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrato && (
            <div className="p-4 bg-zinc-800 rounded-lg mb-4 space-y-3">
              {/* Desglose del precio */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Precio Base</p>
                  <p className="font-mono font-bold text-white">
                    {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(selectedTrato.precio_base || (selectedTrato.precio_final - (selectedTrato.enganche || 0)))?.toLocaleString('es-MX')}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Enganche</p>
                  <p className="font-mono font-bold text-white">
                    {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${selectedTrato.enganche?.toLocaleString('es-MX')}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Pagado</p>
                  <p className="font-mono font-bold text-emerald-400">
                    {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(selectedTrato.total_pagado || 0).toLocaleString('es-MX')}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500">Deuda Total</p>
                  <p className={`font-mono font-bold ${(selectedTrato.deuda || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(selectedTrato.deuda || 0).toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
              
              {/* Mostrar interés de financiamiento si existe */}
              {(selectedTrato.total_interes_financiamiento > 0 || selectedTrato.interes_venta > 0) && (
                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-[#D4AF37]">Interés de Financiamiento</p>
                      <p className="font-mono font-bold text-[#D4AF37]">
                        + {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(selectedTrato.total_interes_financiamiento || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#D4AF37]">% Interés Mensual</p>
                      <p className="font-mono font-bold text-[#D4AF37]">
                        {selectedTrato.interes_venta || 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[#D4AF37]">Total del Contrato</p>
                      <p className="font-mono font-bold text-[#D4AF37]">
                        {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${(selectedTrato.total_contrato || selectedTrato.precio_final).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mostrar recargos por atraso si existen */}
              {selectedTrato.total_recargos > 0 && (
                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-red-400">Recargos por Atraso</p>
                      <p className="font-mono font-bold text-red-400">
                        + {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${selectedTrato.total_recargos?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-red-400">% Interés Diario</p>
                      <p className="font-mono font-bold text-red-400">
                        {selectedTrato.interes_atraso || 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-amber-400">Total con Recargos</p>
                      <p className="font-mono font-bold text-amber-400">
                        {selectedTrato.moneda === 'MXN' ? 'MXN ' : 'USD '}${selectedTrato.precio_con_recargos?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-2">
              {pagosDelTrato.map((pago) => {
                const estadoVisual = calcularEstadoVisual(pago);
                const pendiente = pago.monto_programado - (pago.monto_pagado || 0);
                const simboloMoneda = selectedTrato?.moneda === 'MXN' ? 'MXN ' : 'USD ';
                const tieneInteres = pago.interes_pendiente && pago.interes_pendiente > 0;
                const montoTotal = tieneInteres ? pago.monto_total_con_interes : pago.monto_programado;
                
                return (
                  <div 
                    key={pago.pago_id}
                    className={`p-4 rounded-lg border ${
                      estadoVisual === 'pagado' ? 'bg-emerald-500/10 border-emerald-500/30' :
                      estadoVisual === 'atrasado' ? 'bg-red-500/10 border-red-500/30' :
                      estadoVisual === 'vence_hoy' ? 'bg-red-500/10 border-red-500/30' :
                      estadoVisual === 'proximo' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-zinc-800 border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[60px]">
                          <p className="text-xs text-zinc-500">Pago</p>
                          <p className="text-2xl font-black text-white">#{pago.numero_pago}</p>
                        </div>
                        <div>
                          <p className="text-sm text-zinc-400">
                            Programado: {formatDateSafe(pago.fecha_programada, { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          {pago.fecha_pago && (
                            <p className="text-xs text-emerald-400">
                              Pagado: {formatDateSafe(pago.fecha_pago, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          <p className="font-mono font-bold text-lg text-white">
                            {simboloMoneda}${pago.monto_programado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            {pago.interes_mes > 0 && (
                              <span className="text-xs text-amber-400 ml-2">(incluye interés)</span>
                            )}
                          </p>
                          
                          {/* 🔥 Desglose de amortización (interés de financiamiento y capital) */}
                          {pago.interes_mes !== undefined && pago.interes_mes > 0 && (
                            <div className="mt-2 p-2 bg-zinc-900/80 rounded border border-zinc-700">
                              <p className="text-xs text-zinc-500 mb-1">Desglose del pago:</p>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-amber-400">Interés:</span>
                                  <span className="text-amber-400 font-mono font-bold">${pago.interes_mes?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-cyan-400">Capital:</span>
                                  <span className="text-cyan-400 font-mono font-bold">${pago.capital_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                </div>
                              </div>
                              {pago.saldo_final_mes !== undefined && (
                                <div className="mt-2 pt-2 border-t border-zinc-700 flex justify-between">
                                  <span className="text-zinc-400 text-sm">Saldo restante:</span>
                                  <span className={`font-mono font-bold ${pago.saldo_final_mes === 0 ? 'text-emerald-400' : 'text-white'}`}>
                                    ${pago.saldo_final_mes?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Mostrar interés pendiente (para pagos NO pagados) */}
                          {tieneInteres && estadoVisual !== 'pagado' && (
                            <div className="mt-1 p-2 bg-red-500/20 rounded border border-red-500/30">
                              <p className="text-xs text-red-400">
                                ⚠️ {pago.dias_atraso} días de atraso
                              </p>
                              <p className="text-xs text-red-400">
                                + Interés: {simboloMoneda}${pago.interes_pendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                              <p className="text-sm font-bold text-red-300 mt-1">
                                Total a pagar: {simboloMoneda}${montoTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          )}
                          
                          {/* Mostrar interés pagado (para pagos YA completados) */}
                          {pago.interes_generado > 0 && estadoVisual === 'pagado' && (
                            <div className="mt-1 p-2 bg-amber-500/20 rounded border border-amber-500/30">
                              <p className="text-xs text-amber-400">
                                Interés por atraso pagado: {simboloMoneda}${pago.interes_generado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-emerald-400 font-bold">
                                Total pagado: {simboloMoneda}${pago.monto_pagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          )}
                          
                          {/* 🔥 NUEVO: Mostrar monto original en pesos si el cliente pagó en MXN */}
                          {pago.moneda_pago && pago.moneda_pago !== (selectedTrato?.moneda || 'USD') && pago.monto_pagado_original > 0 && (
                            <div className="mt-1 p-2 bg-blue-500/20 rounded border border-blue-500/30">
                              <p className="text-xs text-blue-300">
                                💵 Cliente pagó en {pago.moneda_pago === 'MXN' ? '🇲🇽 Pesos' : '🇺🇸 Dólares'}:
                              </p>
                              <p className={`text-sm font-bold font-mono ${pago.moneda_pago === 'MXN' ? 'text-amber-400' : 'text-emerald-400'}`}>
                                {pago.moneda_pago} ${pago.monto_pagado_original.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                              {pago.tipo_cambio && pago.tipo_cambio > 0 && (
                                <p className="text-xs text-zinc-400">
                                  TC: {pago.tipo_cambio} → {simboloMoneda}${pago.monto_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Abonos parciales */}
                          {pago.monto_pagado > 0 && pago.monto_pagado < pago.monto_programado && estadoVisual !== 'pagado' && (
                            <p className="text-xs text-emerald-400 mt-1">
                              Abonado: {simboloMoneda}${pago.monto_pagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                          )}
                        </div>
                        {getEstadoBadge(estadoVisual)}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Botón Pagaré - siempre visible */}
                        <Button
                          size="sm"
                          variant={pago.pagare_entregado ? "default" : "outline"}
                          onClick={(e) => { e.stopPropagation(); handleTogglePagare(pago); }}
                          className={pago.pagare_entregado 
                            ? "bg-slate-600 hover:bg-slate-500 text-white font-bold"
                            : "border-slate-500/50 text-slate-400 hover:bg-slate-500/10"
                          }
                          title={pago.pagare_entregado ? "Pagaré entregado ✓" : "Marcar pagaré como entregado"}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          {pago.pagare_entregado ? "Pagaré ✓" : "Pagaré"}
                        </Button>
                        
                        {estadoVisual !== 'pagado' && pendiente > 0 && (
                          <>
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleOpenMarcarPagado(pago); }}
                              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Pagado
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleOpenQuickPay(pago); }}
                              className="border-zinc-600 text-white hover:bg-zinc-700"
                            >
                              <DollarSign className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleOpenReprogram(pago); }}
                              className="border-zinc-600 text-white hover:bg-zinc-700"
                            >
                              <CalendarClock className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { 
                                e.stopPropagation();
                                const cliente = clientes.find(c => c.cliente_id === selectedTrato?.cliente_id);
                                const clienteNombre = cliente?.nombre_completo?.replace(/\s+/g, '_') || 'Cliente';
                                downloadPDF(
                                  `${BACKEND_URL}/api/pagos/${pago.pago_id}/invoice`,
                                  `Cobro_${clienteNombre}_M${pago.numero_pago}.pdf`
                                );
                              }}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                              title="Descargar Invoice de Cobranza"
                            >
                              <FileDown className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {/* Botón REVERTIR - solo si tiene monto pagado */}
                        {pago.monto_pagado > 0 && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleOpenEditFecha(pago); }}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                              title="Editar fecha de pago"
                            >
                              <CalendarClock className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); setRevertDialog({ open: true, pago }); }}
                              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                              title="Revertir pago"
                            >
                              <Undo2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, pago }); }}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo Pago Rápido */}
      <Dialog open={quickPayDialogOpen} onOpenChange={setQuickPayDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Registrar Pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickPaySubmit} className="space-y-3">
            {selectedPago && selectedTrato && (
              <div className="bg-zinc-800 p-3 rounded-lg text-sm space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-500">Monto a pagar:</span>
                  <span className="font-bold text-amber-400 font-mono">
                    {selectedTrato.moneda === 'MXN' ? 'MXN' : 'USD'} ${selectedPago.monto_programado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                
                {/* Desglose de interés y capital si aplica */}
                {selectedPago.interes_mes > 0 && (
                  <div className="bg-zinc-900 p-2 rounded border border-zinc-700">
                    <p className="text-xs text-zinc-500 mb-1">Este pago incluye:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex justify-between">
                        <span className="text-amber-400 text-xs">Interés:</span>
                        <span className="text-amber-400 font-mono text-xs font-bold">${selectedPago.interes_mes?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-cyan-400 text-xs">Capital:</span>
                        <span className="text-cyan-400 font-mono text-xs font-bold">${selectedPago.capital_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-zinc-500">Fecha programada:</span>
                  <span className="text-white">
                    {formatDateSafe(selectedPago.fecha_programada, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                {selectedPago.monto_pagado > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-500">Ya abonado:</span>
                    <span className="text-emerald-400 font-mono">
                      {selectedTrato?.moneda === 'MXN' ? 'MXN' : 'USD'} ${selectedPago.monto_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                  <span className="text-zinc-400 font-medium">Pendiente:</span>
                  <span className="text-amber-400 font-bold font-mono">
                    {selectedTrato?.moneda === 'MXN' ? 'MXN' : 'USD'} ${(selectedPago.monto_programado - (selectedPago.monto_pagado || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
            
            {/* Selector: Pago Completo o Abono */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const pendiente = selectedPago ? (selectedPago.monto_programado - (selectedPago.monto_pagado || 0)) : 0;
                  setQuickPayFormData({...quickPayFormData, monto: pendiente.toFixed(2), tipo_pago_parcial: false});
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                  !quickPayFormData.tipo_pago_parcial 
                    ? 'bg-emerald-500 text-black' 
                    : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                }`}
              >
                ✅ Pago Completo
              </button>
              <button
                type="button"
                onClick={() => setQuickPayFormData({...quickPayFormData, monto: '', tipo_pago_parcial: true})}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                  quickPayFormData.tipo_pago_parcial 
                    ? 'bg-amber-500 text-black' 
                    : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                }`}
              >
                💰 Abonar
              </button>
            </div>
            
            <div>
              <Label className="text-zinc-300">Fecha real de pago</Label>
              <Input type="date" value={quickPayFormData.fecha_pago} onChange={(e) => setQuickPayFormData({...quickPayFormData, fecha_pago: e.target.value})} required className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            
            {/* Monto y Moneda de Pago */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 space-y-2">
              {/* Selector de moneda de pago */}
              <div>
                <Label className="text-zinc-300 mb-1 block text-sm">¿En qué moneda se hizo el pago?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setQuickPayFormData({...quickPayFormData, moneda_pago: 'USD'})}
                    className={`py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                      quickPayFormData.moneda_pago === 'USD' || !quickPayFormData.moneda_pago
                        ? 'bg-emerald-500 text-black' 
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                    }`}
                  >
                    🇺🇸 Dólares (USD)
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickPayFormData({...quickPayFormData, moneda_pago: 'MXN'})}
                    className={`py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                      quickPayFormData.moneda_pago === 'MXN'
                        ? 'bg-amber-500 text-black' 
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                    }`}
                  >
                    🇲🇽 Pesos (MXN)
                  </button>
                </div>
              </div>

              {/* Monto en la moneda seleccionada */}
              <div>
                <Label className={`font-medium ${quickPayFormData.moneda_pago === 'MXN' ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {quickPayFormData.tipo_pago_parcial ? 'Monto a Abonar' : 'Monto Pagado'} ({quickPayFormData.moneda_pago === 'MXN' ? 'MXN' : 'USD'})
                </Label>
                <div className="relative mt-1">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-bold ${quickPayFormData.moneda_pago === 'MXN' ? 'text-amber-400' : 'text-emerald-400'}`}>$</span>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={quickPayFormData.monto} 
                    onChange={(e) => setQuickPayFormData({...quickPayFormData, monto: e.target.value})} 
                    required 
                    className={`bg-zinc-900 text-white text-xl font-mono pl-8 ${quickPayFormData.moneda_pago === 'MXN' ? 'border-amber-500/30' : 'border-emerald-500/30'}`}
                    placeholder={quickPayFormData.tipo_pago_parcial ? "Ingresa el abono" : "0.00"}
                  />
                </div>
              </div>
              
              {/* Tipo de Cambio */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-blue-400 font-medium">Tipo de Cambio (MXN por 1 USD)</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={quickPayFormData.tipo_cambio} 
                    onChange={(e) => setQuickPayFormData({...quickPayFormData, tipo_cambio: e.target.value})} 
                    required 
                    className="bg-zinc-900 border-blue-500/30 text-white text-lg font-mono mt-1" 
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={actualizarTipoCambio}
                  disabled={loadingTipoCambio}
                  className="mt-6 border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingTipoCambio ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* Equivalente en la otra moneda */}
              <div className={`rounded-lg p-3 ${quickPayFormData.moneda_pago === 'MXN' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                <Label className={quickPayFormData.moneda_pago === 'MXN' ? 'text-emerald-400 font-medium' : 'text-amber-400 font-medium'}>
                  Equivalente en {quickPayFormData.moneda_pago === 'MXN' ? 'Dólares (USD)' : 'Pesos (MXN)'}
                </Label>
                <p className={`text-2xl font-bold font-mono mt-1 ${quickPayFormData.moneda_pago === 'MXN' ? 'text-emerald-400' : 'text-amber-400'}`}>
                  ${quickPayFormData.moneda_pago === 'MXN' 
                    ? (parseFloat(quickPayFormData.monto || 0) / parseFloat(quickPayFormData.tipo_cambio || 1)).toLocaleString('es-MX', { minimumFractionDigits: 2 })
                    : (parseFloat(quickPayFormData.monto || 0) * parseFloat(quickPayFormData.tipo_cambio || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })
                  }
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {quickPayFormData.moneda_pago === 'MXN' 
                    ? `${quickPayFormData.monto || 0} MXN ÷ ${quickPayFormData.tipo_cambio} = USD`
                    : `${quickPayFormData.monto || 0} USD × ${quickPayFormData.tipo_cambio} = MXN`
                  }
                </p>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Método</Label>
              <Select value={quickPayFormData.metodo_pago} onValueChange={(v) => setQuickPayFormData({...quickPayFormData, metodo_pago: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="efectivo" className="text-white hover:bg-zinc-700">💵 Efectivo</SelectItem>
                  <SelectItem value="banco" className="text-white hover:bg-zinc-700">🏦 Banco</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className={`w-full font-bold ${quickPayFormData.tipo_pago_parcial ? 'bg-amber-500 hover:bg-amber-400' : 'bg-emerald-500 hover:bg-emerald-400'} text-black`}>
              {quickPayFormData.tipo_pago_parcial ? '💰 Confirmar Abono' : '✅ Confirmar Pago Completo'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diálogo Reprogramar */}
      <Dialog open={reprogramDialogOpen} onOpenChange={setReprogramDialogOpen}>
        <DialogContent className="sm:max-w-sm bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Reprogramar Fecha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReprogramSubmit} className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg text-sm">
              <p className="text-amber-400 font-medium">Acción excepcional</p>
              <p className="text-zinc-400 mt-1">Solo usar en casos especiales.</p>
            </div>
            <div>
              <Label className="text-zinc-300">Nueva Fecha</Label>
              <Input type="date" value={reprogramFormData.nueva_fecha} onChange={(e) => setReprogramFormData({nueva_fecha: e.target.value})} required className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">Reprogramar</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diálogo Editar Fecha de Pago */}
      <Dialog open={editFechaDialogOpen} onOpenChange={setEditFechaDialogOpen}>
        <DialogContent className="sm:max-w-sm bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-500" />
              Editar Fecha de Pago
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditFechaSubmit} className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg text-sm">
              <p className="text-blue-400 font-medium">Pago #{editFechaData.pago?.numero_pago}</p>
              <p className="text-zinc-400 mt-1">Modifica la fecha en que se realizó el pago.</p>
            </div>
            <div>
              <Label className="text-zinc-300">Fecha de Pago</Label>
              <Input 
                type="date" 
                value={editFechaData.nueva_fecha} 
                onChange={(e) => setEditFechaData({...editFechaData, nueva_fecha: e.target.value})} 
                required 
                className="bg-zinc-800 border-zinc-700 text-white" 
              />
            </div>
            <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-400 text-white font-bold">
              Guardar Fecha
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Marcar Pagado con Fecha y Conversión de Moneda */}
      <Dialog open={marcarPagadoDialog.open} onOpenChange={(open) => setMarcarPagadoDialog({...marcarPagadoDialog, open})}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Registrar Pago
              {/* Badge de moneda del trato */}
              {selectedTrato && (
                <span className={`ml-2 px-2 py-0.5 text-xs font-bold rounded ${
                  selectedTrato.moneda === 'MXN' 
                    ? 'bg-amber-500/20 text-amber-400' 
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  Trato en {selectedTrato.moneda === 'MXN' ? '🇲🇽 MXN' : '🇺🇸 USD'}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Info del pago pendiente */}
            <div className="bg-zinc-800 border border-zinc-700 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Pago #{marcarPagadoDialog.pago?.numero_pago}</span>
                <span className="text-sm text-zinc-500">
                  Pendiente en {selectedTrato?.moneda || 'USD'}
                </span>
              </div>
              <p className="text-2xl font-bold text-white font-mono mt-1">
                {selectedTrato?.moneda === 'MXN' ? 'MXN' : 'USD'} ${((marcarPagadoDialog.pago?.monto_programado || 0) - (marcarPagadoDialog.pago?.monto_pagado || 0) + (marcarPagadoDialog.pago?.interes_pendiente || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </p>
            </div>
            
            {/* Selector de moneda en que pagó el cliente */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 space-y-3">
              <div>
                <Label className="text-zinc-300 font-medium">¿En qué moneda pagó el cliente?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMarcarPagadoMoneda('USD');
                      // Recalcular monto sugerido
                      const pendiente = (marcarPagadoDialog.pago?.monto_programado || 0) - (marcarPagadoDialog.pago?.monto_pagado || 0) + (marcarPagadoDialog.pago?.interes_pendiente || 0);
                      const monedaTrato = selectedTrato?.moneda || 'USD';
                      if (monedaTrato === 'USD') {
                        setMarcarPagadoMontoOriginal(pendiente);
                      } else {
                        // Trato en MXN, pago en USD -> convertir
                        setMarcarPagadoMontoOriginal(pendiente / marcarPagadoTipoCambio);
                      }
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                      marcarPagadoMoneda === 'USD'
                        ? 'bg-emerald-500 text-black' 
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                    }`}
                  >
                    🇺🇸 Dólares (USD)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMarcarPagadoMoneda('MXN');
                      // Recalcular monto sugerido
                      const pendiente = (marcarPagadoDialog.pago?.monto_programado || 0) - (marcarPagadoDialog.pago?.monto_pagado || 0) + (marcarPagadoDialog.pago?.interes_pendiente || 0);
                      const monedaTrato = selectedTrato?.moneda || 'USD';
                      if (monedaTrato === 'MXN') {
                        setMarcarPagadoMontoOriginal(pendiente);
                      } else {
                        // Trato en USD, pago en MXN -> convertir
                        setMarcarPagadoMontoOriginal(pendiente * marcarPagadoTipoCambio);
                      }
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-bold transition-colors ${
                      marcarPagadoMoneda === 'MXN'
                        ? 'bg-amber-500 text-black' 
                        : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                    }`}
                  >
                    🇲🇽 Pesos (MXN)
                  </button>
                </div>
              </div>
              
              {/* Campo de monto (editable) */}
              <div>
                <Label className={`font-medium ${marcarPagadoMoneda === 'MXN' ? 'text-amber-400' : 'text-emerald-400'}`}>
                  Monto recibido ({marcarPagadoMoneda})
                </Label>
                <div className="relative mt-1">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-bold ${marcarPagadoMoneda === 'MXN' ? 'text-amber-400' : 'text-emerald-400'}`}>$</span>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={marcarPagadoMontoOriginal} 
                    onChange={(e) => setMarcarPagadoMontoOriginal(parseFloat(e.target.value) || 0)} 
                    className={`bg-zinc-900 text-white text-lg font-mono pl-8 ${marcarPagadoMoneda === 'MXN' ? 'border-amber-500/30' : 'border-emerald-500/30'}`}
                  />
                </div>
              </div>
              
              {/* Tipo de cambio - solo si hay conversión */}
              {marcarPagadoMoneda !== (selectedTrato?.moneda || 'USD') && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Label className="text-blue-400 font-medium">Tipo de Cambio (MXN por 1 USD)</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={marcarPagadoTipoCambio} 
                        onChange={(e) => setMarcarPagadoTipoCambio(parseFloat(e.target.value) || 17.50)} 
                        className="bg-zinc-900 border-blue-500/30 text-white text-lg font-mono mt-1" 
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={actualizarTipoCambio}
                      disabled={loadingTipoCambio}
                      className="mt-6 border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingTipoCambio ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  
                  {/* Preview de conversión */}
                  <div className={`rounded-lg p-3 ${
                    (selectedTrato?.moneda || 'USD') === 'MXN' 
                      ? 'bg-amber-500/10 border border-amber-500/30' 
                      : 'bg-emerald-500/10 border border-emerald-500/30'
                  }`}>
                    <Label className={(selectedTrato?.moneda || 'USD') === 'MXN' ? 'text-amber-400 font-medium' : 'text-emerald-400 font-medium'}>
                      Se aplicarán al saldo del trato:
                    </Label>
                    <p className={`text-2xl font-bold font-mono mt-1 ${
                      (selectedTrato?.moneda || 'USD') === 'MXN' ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                      {(selectedTrato?.moneda || 'USD') === 'MXN' ? 'MXN' : 'USD'} $
                      {(marcarPagadoMoneda === 'MXN' && (selectedTrato?.moneda || 'USD') === 'USD'
                        ? (marcarPagadoMontoOriginal / marcarPagadoTipoCambio)
                        : marcarPagadoMoneda === 'USD' && (selectedTrato?.moneda || 'USD') === 'MXN'
                          ? (marcarPagadoMontoOriginal * marcarPagadoTipoCambio)
                          : marcarPagadoMontoOriginal
                      ).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {marcarPagadoMoneda === 'MXN' 
                        ? `${marcarPagadoMontoOriginal.toLocaleString('es-MX')} MXN ÷ ${marcarPagadoTipoCambio} = USD`
                        : `${marcarPagadoMontoOriginal.toLocaleString('es-MX')} USD × ${marcarPagadoTipoCambio} = MXN`
                      }
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div>
              <Label className="text-zinc-300">¿Cuándo se realizó el pago?</Label>
              <Input 
                type="date" 
                value={marcarPagadoFecha} 
                onChange={(e) => setMarcarPagadoFecha(e.target.value)} 
                className="bg-zinc-800 border-zinc-700 text-white mt-1" 
              />
              <p className="text-xs text-zinc-500 mt-1">Selecciona la fecha real del pago</p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Método de pago</Label>
              <Select value={marcarPagadoMetodo} onValueChange={setMarcarPagadoMetodo}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={handleConfirmarPagado}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmar Pago
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Eliminar */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar Pago #{deleteDialog.pago?.numero_pago}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog Revertir Pago */}
      <AlertDialog open={revertDialog.open} onOpenChange={(open) => setRevertDialog({...revertDialog, open})}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Revertir Pago #{revertDialog.pago?.numero_pago}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esto quitará el monto pagado de <span className="text-amber-400 font-bold">${revertDialog.pago?.monto_pagado?.toLocaleString('es-MX')}</span> y volverá el pago a estado pendiente.
              <br /><br />
              Usa esto si registraste el pago por error.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevert} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Revertir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
